import { LGraphNode, LiteGraph } from "litegraph.js";
import { addDrivableSlider, finalizeDrivableSliders, syncDrivableSliders } from "../utils/addDrivableSlider.js";

/**
 * MouseTrailNode
 * --------------
 * Draws mouse movement as a trail/painting on a canvas.
 * Outputs a canvas texture you can blend, mask, or feed into effects.
 */
export class MouseTrailNode extends LGraphNode {
  constructor() {
    super();
    this.title = "Mouse Trail";

    this.properties = {
      active: true,
      brushSize: 20,
      decay: 0.98,
      color: "#ffffff",
      canvasSize: 1500,
    };

    this.addWidget("toggle", "Active", true, (val) => {
      this.properties.active = val;
    });

    this._lastX = -1;
    this._lastY = -1;
    this._mouseX = 0;
    this._mouseY = 0;

    this.addInput("Color", "string");
    this.addOutput("Canvas", "canvas");

    addDrivableSlider(this, "Brush Size", "brushSize", 20, 1, 200, 1);
    addDrivableSlider(this, "Decay", "decay", 0.98, 0.9, 1, 0.005);

    finalizeDrivableSliders(this, 1);

    this._outCanvas = document.createElement("canvas");
    this._outCanvas.width = this.properties.canvasSize;
    this._outCanvas.height = this.properties.canvasSize;
    this._outCtx = this._outCanvas.getContext("2d");

    this._onMouseMove = (e) => {
      if (!this.properties.active) return;
      this._mouseX = e.clientX / window.innerWidth;
      this._mouseY = e.clientY / window.innerHeight;
    };
    window.addEventListener("mousemove", this._onMouseMove);

    this.size = [200, 120];
    this.mode = LiteGraph.ALWAYS;
    this.color = "#2a2a2a";
    this.bgcolor = "#333333";
  }

  onDrawForeground(ctx) {
    const w = this.size[0];
    const h = this.size[1];

    // Mini preview of the trail canvas
    const previewY = h - 30;
    const previewW = w - 20;
    const previewH = 20;

    ctx.fillStyle = "rgba(0,0,0,0.3)";
    ctx.fillRect(10, previewY, previewW, previewH);
    ctx.drawImage(this._outCanvas, 10, previewY, previewW, previewH);
  }

  onExecute() {
    syncDrivableSliders(this);

    const inputColor = this.getInputData(0);
    if (inputColor) this.properties.color = inputColor;

    const { brushSize, decay, color, canvasSize } = this.properties;
    const ctx = this._outCtx;

    // Apply decay (fade out old trails)
    if (decay < 1) {
      ctx.globalCompositeOperation = "destination-in";
      ctx.fillStyle = `rgba(255,255,255,${decay})`;
      ctx.fillRect(0, 0, canvasSize, canvasSize);
      ctx.globalCompositeOperation = "source-over";
    }

    // Map mouse position to canvas coords
    const x = this._mouseX * canvasSize;
    const y = this._mouseY * canvasSize;

    // Only draw new strokes when active
    if (this.properties.active) {
      // Draw brush stroke
      if (this._lastX >= 0) {
        ctx.strokeStyle = color;
        ctx.lineWidth = brushSize;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.beginPath();
        ctx.moveTo(this._lastX, this._lastY);
        ctx.lineTo(x, y);
        ctx.stroke();
      }

      this._lastX = x;
      this._lastY = y;
    }

    this.setOutputData(0, this._outCanvas);
  }

  onRemoved() {
    window.removeEventListener("mousemove", this._onMouseMove);
  }
}

LiteGraph.registerNodeType("input/MouseTrail", MouseTrailNode);
